import React, { useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Input,
  UncontrolledTooltip,
  Col,
  Row,
  Label,
  PopoverBody,
  UncontrolledPopover,
} from "reactstrap";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import {
  ISOToHumanReadableDate,
  getDuration,
} from "components/CommonComponents/CommonFunctions";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const SessionsList = [
  {
    id: 1,
    sessionName: "Electrical Safety Basics",
    instructorName: "John Doe",
    sessionDate: "2026-01-28T00:00:00Z",
    sessionStartTime: "09:00 AM",
    sessionEndTime: "11:50 AM",
    description:
      "Covers fundamental electrical safety principles, common hazards, and preventive measures to ensure safe working practices.",
  },
  {
    id: 2,
    sessionName: "Introduction to Circuit Theory",
    instructorName: "Jane Smith",
    sessionDate: "2026-01-28T00:00:00Z",
    sessionStartTime: "11:30 AM",
    sessionEndTime: "01:00 PM",
    description:
      "Introduces basic circuit concepts including voltage, current, resistance, and simple circuit analysis techniques.",
  },
  {
    id: 3,
    sessionName: "AC/DC Fundamentals",
    instructorName: "John Doe",
    sessionDate: "2026-01-29T00:00:00Z",
    sessionStartTime: "10:00 AM",
    sessionEndTime: "12:00 PM",
    description:
      "Explains the fundamentals of alternating current and direct current, including characteristics, applications, and differences.",
  },
  {
    id: 4,
    sessionName: "Wiring and Cabling Standards",
    instructorName: "Mary Johnson",
    sessionDate: "2026-01-29T00:00:00Z",
    sessionStartTime: "01:00 PM",
    sessionEndTime: "03:15 PM",
    description:
      "Focuses on industry-standard wiring practices, cable types, installation guidelines, and compliance requirements.",
  },
  {
    id: 5,
    sessionName: "Electrical Machines Overview",
    instructorName: "Jane Smith",
    sessionDate: "2026-01-30T00:00:00Z",
    sessionStartTime: "09:30 AM",
    sessionEndTime: "11:45 AM",
    description:
      "Provides an overview of common electrical machines such as motors and generators, including basic operation principles.",
  },
  {
    id: 6,
    sessionName: "Transformers and Generators",
    instructorName: "John Doe",
    sessionDate: "2026-01-30T00:00:00Z",
    sessionStartTime: "12:00 PM",
    sessionEndTime: "02:50 PM",
    description:
      "Covers transformer and generator construction, working principles, efficiency considerations, and practical use cases.",
  },
  {
    id: 7,
    sessionName: "Power Distribution Systems",
    instructorName: "Mary Johnson",
    sessionDate: "2026-01-31T00:00:00Z",
    sessionStartTime: "10:00 AM",
    sessionEndTime: "12:10 PM",
    description:
      "Explains electrical power distribution systems, components, load management, and system reliability concepts.",
  },
  {
    id: 8,
    sessionName: "Electrical Safety Testing & Inspection",
    instructorName: "John Doe",
    sessionDate: "2026-01-31T00:00:00Z",
    sessionStartTime: "01:00 PM",
    sessionEndTime: "03:30 PM",
    description:
      "Focuses on electrical testing methods, inspection procedures, and safety compliance to identify potential risks.",
  },
  {
    id: 9,
    sessionName: "Renewable Energy Systems",
    instructorName: "Jane Smith",
    sessionDate: "2026-02-01T00:00:00Z",
    sessionStartTime: "09:30 AM",
    sessionEndTime: "11:00 AM",
    description:
      "Introduces renewable energy technologies such as solar and wind, along with system components and benefits.",
  },
  {
    id: 10,
    sessionName: "Industrial Automation Basics",
    instructorName: "Mary Johnson",
    sessionDate: "2026-02-01T00:00:00Z",
    sessionStartTime: "11:30 AM",
    sessionEndTime: "01:30 PM",
    description:
      "Covers the basics of industrial automation, including sensors, controllers, and automation workflows.",
  },
];

function CourseSchedule({ courseId }) {
  const [pageNum, setPageNum] = useState(1);
  const [searchState, setSearchState] = useState({
    name: "",
    filterParams: [],
  });

  const handleClear = () => {
    setSearchState({
      name: "",
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {};

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "id",
    },
    {
      Header: "Session Name",
      accessor: "sessionName",
      minWidth: 160,
      Cell: ({ row }) => {
        const { sessionName, id } = row.original;
        if (!sessionName) return "-";
        return (
          <>
            <span id={`session-${id}`} className="ellipsis-text">
              {sessionName}
            </span>
            <UncontrolledPopover
              target={`session-${id}`}
              trigger="hover"
              placement="top"
            >
              <PopoverBody>{sessionName}</PopoverBody>
            </UncontrolledPopover>
          </>
        );
      },
    },
    {
      Header: "Instructor",
      accessor: "instructorName",
    },
    {
      Header: "Date",
      accessor: "sessionDate",
      Cell: ({ value }) => ISOToHumanReadableDate(value),
    },
    {
      Header: "Time",
      accessor: "sessionStartTime",
      Cell: ({ row }) => {
        const { sessionStartTime, sessionEndTime } = row.original;

        return (
          <div>
            <div className="d-flex align-items-center mb-1">
              <span className="mr-1">From:</span>
              <span>{sessionStartTime}</span>
            </div>
            <div className="d-flex align-items-center">
              <span className="mr-1">To:</span>
              <span className="pl-3">{sessionEndTime}</span>
            </div>
          </div>
        );
      },
    },
    {
      Header: "Total Duration",
      accessor: "totalDuration",
      Cell: ({ row }) => {
        const { sessionStartTime, sessionEndTime } = row.original;
        return getDuration(sessionStartTime, sessionEndTime);
      },
    },
    {
      Header: "Description",
      accessor: "description",
      minWidth: 160,
      Cell: ({ row }) => {
        const { description, id } = row.original;
        if (!description) return "-";
        return (
          <>
            <span id={`desc-${id}`} className="ellipsis-text">
              {description}
            </span>

            <UncontrolledPopover
              target={`desc-${id}`}
              trigger="hover"
              placement="top"
            >
              <PopoverBody>{description}</PopoverBody>
            </UncontrolledPopover>
          </>
        );
      },
    },
  ];

  return (
    <>
      <Row className="mb-6">
        <Col md="12">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                {/* Left side - Filter Inputs + Search */}
                <div className="d-flex flex-wrap align-items-center filter-group">
                  <Input
                    type="text"
                    placeholder="Search by session "
                    className="mr-2 mb-2 filter-input-large"
                    value={searchState.searchString}
                    onChange={(e) =>
                      setSearchState((prev) => ({
                        ...prev,
                        searchString: e.target.value,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />

                  <Input
                    type="text"
                    placeholder="Search by instructor name "
                    className="mr-2 mb-2 filter-input-large"
                    value={searchState.searchString}
                    onChange={(e) =>
                      setSearchState((prev) => ({
                        ...prev,
                        searchString: e.target.value,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />

                  <div className="d-flex align-items-center mx-2 mb-2">
                    <span className="text-muted mb-0 mr-2">Schedule Date</span>
                    <DatePicker
                      selected={SessionsList.sessionStartTime}
                      //   onChange={handleScheduleChange}
                      startDate={SessionsList.sessionStartTime}
                      endDate={SessionsList.sessionEndTime}
                      selectsRange
                      isClearable
                      placeholderText="Select a date or range"
                      className="form-control"
                      dateFormat="dd-MM-yyyy"
                    />
                  </div>
                  <Button
                    color="default"
                    // className="d-flex align-items-center btn-deep-blue mb-0"
                    className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                    onClick={handleSearch}
                  >
                    <i className="fa fa-search mr-1" />
                    Search
                  </Button>

                  {Object.keys(searchState.filterParams).length > 0 && (
                    <Button
                      color="default"
                      className="ml-2 mb-0 filter-btn btn-simple"
                      onClick={handleClear}
                    >
                      <i className="fa fa-times mr-1" /> Clear
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-2 mb-3 mx-1">
                {SessionsList.length ? (
                  <span className="total-records">
                    Total Records : {SessionsList.length}
                  </span>
                ) : null}
              </div>

              <CommonTable
                columns={columns}
                data={SessionsList.slice(
                  (pageNum - 1) * DEFAULT_RECORDS_PER_PAGE,
                  pageNum * DEFAULT_RECORDS_PER_PAGE,
                )}
                defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                activePage={pageNum}
                setPageNum={setPageNum}
                totalPages={Math.ceil(
                  SessionsList.length / DEFAULT_RECORDS_PER_PAGE,
                )}
                showPaginationBottom
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default CourseSchedule;
